/**
 * Iter-157G P1 — Schedule preview page (OSF-chromed).
 *
 * Schedules previously opened in a new browser tab via a raw file
 * URL, breaking the consistency promise pilot users called out:
 *
 *   - PDFs in Notes opened inside the OSF viewer.
 *   - PDFs in Schedules opened a new tab.
 *   - DOCX "Open in new tab" actually downloaded.
 *
 * This page renders the same in-app chrome as DocumentViewerPage so
 * the experience is identical regardless of where the file lives.
 * For inline-renderable types (PDF / images / text) we use an
 * iframe. For DOCX / XLSX / PPTX / ZIP we fall back to a calm
 * "Preview not available" card with a single Download action — no
 * dead controls, no misleading "Open in new tab" that just
 * downloads.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Download,
    FileText,
    Loader2,
    Printer,
} from "lucide-react";
import ProjectHeader from "../components/ProjectHeader";
import { Button } from "../components/ui/button";
import { api, formatApiError, tokenStore } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { useIsMobileViewport } from "../lib/useIsMobileViewport";
import MobilePdfHandoffCard from "../components/MobilePdfHandoffCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INLINE_EXTS = new Set([
    "pdf",
    "txt",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "webp",
    "heic",
    "heif",
    "svg",
]);

function extOf(name) {
    const n = (name || "").toLowerCase();
    return n.includes(".") ? n.split(".").pop() : "";
}

export default function SchedulePreviewPage() {
    const { projectId, scheduleId } = useParams();
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let aborted = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(
                    `${apiBase}/${projectId}/schedules/${scheduleId}`,
                );
                if (!aborted) setSchedule(data);
            } catch (err) {
                if (!aborted) setError(formatApiError(err));
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [apiBase, projectId, scheduleId]);

    const fileUrl = (download = false) => {
        const token = tokenStore.get() || "";
        const qs = new URLSearchParams();
        if (token) qs.set("token", token);
        if (download) qs.set("download", "1");
        return `${API}${apiBase}/${projectId}/schedules/${scheduleId}/file?${qs.toString()}`;
    };

    const filename = schedule?.filename || schedule?.label || "Schedule";
    const ext = useMemo(() => extOf(filename), [filename]);
    const viewerMode = useMemo(() => {
        if (!schedule) return "loading";
        return INLINE_EXTS.has(ext) ? "inline" : "fallback";
    }, [schedule, ext]);
    // Iter-157K — Universal mobile PDF rule. Phones use the
    // shared MobilePdfHandoffCard; desktop keeps the iframe.
    const isMobile = useIsMobileViewport();

    const onPrint = () => {
        const ifr = document.createElement("iframe");
        ifr.setAttribute("aria-hidden", "true");
        ifr.style.position = "fixed";
        ifr.style.right = "0";
        ifr.style.bottom = "0";
        ifr.style.width = "0";
        ifr.style.height = "0";
        ifr.style.border = "0";
        ifr.style.opacity = "0";
        ifr.src = fileUrl(false);
        let printed = false;
        ifr.addEventListener("load", () => {
            if (printed) return;
            printed = true;
            try {
                ifr.contentWindow?.focus();
                ifr.contentWindow?.print();
            } catch {
                window.print();
            }
            setTimeout(() => {
                try {
                    ifr.remove();
                } catch { /* noop */ }
            }, 1000);
        });
        document.body.appendChild(ifr);
    };

    const onClose = () => {
        // Return to wherever launched the preview. Schedules page
        // is the canonical home so we always end there.
        navigate(`${routeBase}/${projectId}/schedule`);
    };

    return (
        <div
            className="min-h-screen bg-background flex flex-col"
            data-testid="schedule-preview-page"
        >
            <ProjectHeader
                projectName={null /* derived from auth header context */}
                subtitle={schedule?.label || filename || "Schedule"}
            />
            <main className="w-full px-3 sm:px-5 pt-3 pb-32 flex-1 flex flex-col">
                {/* Iter-157L.1 — Schedule preview chrome now mirrors
                    DocumentViewerPage's exact layout (kind pill +
                    big title + filename mono line + sky storage
                    strip + handoff card / iframe + bottom action
                    bar) so a user can't tell whether a PDF came
                    from Notes or Schedules. The previous wrapping
                    `rounded-xl bg-card p-5` container, smaller
                    `text-lg` title, inline FileText icon and
                    redundant top "Back to Schedule" button have
                    been removed. The ProjectHeader chevron + the
                    bottom Close button are the two correct exits,
                    same as DocumentViewerPage. */}

                {loading ? (
                    <div
                        data-testid="schedule-preview-loading"
                        className="flex items-center justify-center gap-2 text-sm text-neutral-500 py-12"
                    >
                        <Loader2 size={14} className="animate-spin" />
                        Loading schedule…
                    </div>
                ) : error ? (
                    <p
                        data-testid="schedule-preview-error"
                        className="text-sm text-red-300"
                    >
                        {error}
                    </p>
                ) : !schedule ? null : (
                    <>
                        <header className="mb-3 max-w-3xl">
                            <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                                <span
                                    data-testid="schedule-preview-kind"
                                    className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5 font-semibold"
                                >
                                    Schedule
                                </span>
                            </div>
                            <h1
                                data-testid="schedule-preview-title"
                                className="font-display text-2xl font-bold text-white break-words"
                            >
                                {schedule.label || filename}
                            </h1>
                            {filename && filename !== (schedule.label || filename) ? (
                                <p className="text-[11px] text-neutral-500 mt-0.5 mono break-all">
                                    {filename}
                                </p>
                            ) : !schedule.label ? null : (
                                <p className="text-[11px] text-neutral-500 mt-0.5 mono break-all">
                                    {filename}
                                </p>
                            )}
                        </header>

                        {/* Iter-157G P1 — same storage strip the
                            DocumentViewerPage uses, so schedules
                            and change documents read identically. */}
                        <div
                            data-testid="schedule-preview-storage-strip"
                            className="mb-3 max-w-3xl rounded-lg border border-sky-500/25 bg-sky-500/[0.06] px-3 py-2 text-[11px] leading-snug flex items-start gap-2 text-sky-100"
                        >
                            <FileText
                                size={12}
                                className="mt-0.5 shrink-0 text-sky-300"
                            />
                            <span>
                                <span className="font-semibold">
                                    Stored in OnSetFlow
                                </span>
                                <span className="text-sky-200/80">
                                    {" "}— streamed securely from your project.{" "}
                                    {isMobile
                                        ? "Tap Open PDF to read in your phone's PDF viewer; tap Close to return."
                                        : "Use Close at the bottom of the screen to return."}
                                </span>
                            </span>
                        </div>

                        {viewerMode === "inline" ? (
                            isMobile ? (
                                <MobilePdfHandoffCard
                                    fileUrl={fileUrl(false)}
                                    filename={filename}
                                    downloadHref={fileUrl(true)}
                                    testidPrefix="schedule-preview-mobile-pdf"
                                />
                            ) : (
                                <iframe
                                    title={filename}
                                    src={fileUrl(false)}
                                    data-testid="schedule-preview-iframe"
                                    className="w-full rounded-md border border-white/10 bg-black h-[calc(100vh-260px)] min-h-[60vh]"
                                />
                            )
                        ) : (
                            <div
                                data-testid="schedule-preview-fallback"
                                className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-5 text-[13px] leading-relaxed text-amber-100"
                            >
                                <p className="font-semibold">
                                    Preview not available for this file type.
                                </p>
                                <p className="mt-1.5 text-neutral-300">
                                    {ext.toUpperCase()} files can&rsquo;t be
                                    rendered directly. Use{" "}
                                    <strong>Download</strong> to open it in the
                                    app you usually use (Word, Pages, Numbers,
                                    Google Docs, etc).
                                </p>
                                <p
                                    data-testid="schedule-preview-fallback-filename"
                                    className="mt-3 text-[11px] mono break-all text-neutral-400"
                                >
                                    {filename}
                                </p>
                            </div>
                        )}

                        {/* Iter-157L.1 — bottom action bar mirrors
                            DocumentViewerPage byte-for-byte (Close
                            on the left, Download + optional Print
                            inline, no ml-auto since we don't have
                            an Archive on schedules). Same h-10
                            buttons, same secondary styling, same
                            wording — the user should not be able to
                            tell which surface they came from. */}
                        <div
                            data-testid="schedule-preview-actions"
                            className="mt-4 max-w-3xl flex flex-wrap gap-2 items-center"
                        >
                            <Button
                                variant="secondary"
                                onClick={onClose}
                                data-testid="schedule-preview-close"
                                className="min-tap"
                            >
                                Close
                            </Button>
                            <a
                                href={fileUrl(true)}
                                data-testid="schedule-preview-download"
                                className="tap inline-flex items-center justify-center gap-1.5 rounded-md bg-secondary border border-white/10 text-neutral-200 hover:bg-white/10 px-3 h-10 text-[13px] font-semibold"
                            >
                                <Download size={14} />
                                Download
                            </a>
                            {viewerMode === "inline" && !isMobile ? (
                                <button
                                    type="button"
                                    data-testid="schedule-preview-print"
                                    onClick={onPrint}
                                    className="tap inline-flex items-center justify-center gap-1.5 rounded-md bg-secondary border border-white/10 text-neutral-200 hover:bg-white/10 px-3 h-10 text-[13px] font-semibold"
                                >
                                    <Printer size={14} />
                                    Print
                                </button>
                            ) : null}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
